import { array, fn, hash } from '@ember/helper';
import { action } from '@ember/object';
import { guidFor } from '@ember/object/internals';
import didInsert from '@ember/render-modifiers/modifiers/did-insert';
import { service } from '@ember/service';
import { isPresent } from '@ember/utils';
import { ensureSafeComponent } from '@embroider/util';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import optional from '@nullvoxpopuli/ember-composable-helpers/helpers/optional';
import changesetSet from 'ember-changeset/helpers/changeset-set';
import { restartableTask } from 'ember-concurrency';
import perform from 'ember-concurrency/helpers/perform';
import { t } from 'ember-intl';
import { and, eq, gt, not, or } from 'ember-truth-helpers';
import BvBtn from 'volta/components/bv-btn';
import BvCard from 'volta/components/bv-card';
import BvCheckbox from 'volta/components/bv-checkbox';
import BvChoiceBlocks from 'volta/components/bv-choice-blocks';
import BvFormLayout from 'volta/components/bv-form-layout';
import BvInline from 'volta/components/bv-inline';
import BvModal from 'volta/components/bv-modal';
import BvSelect from 'volta/components/bv-select';
import BvStack from 'volta/components/bv-stack';
import BvText from 'volta/components/bv-text';
import BvBanner from 'volta/components/bv-banner';
import BvTextField from 'volta/components/bv-text-field';
import CollectionViewFilters from 'volta/components/collection-view/filters';
import CollectionViewGroups from 'volta/components/collection-view/groups';
import CollectionViewProperties from 'volta/components/collection-view/properties';
import CollectionViewSortList from 'volta/components/collection-view/sort-list';
import DomainUserSelect from 'volta/components/domain-user-select';
import UserRoleSelect from 'volta/components/user-role-select';
import UserListItem from 'volta/components/user/list-item';
import can from 'volta/helpers/can';
import cannot from 'volta/helpers/cannot';
import isEmptyObj from 'volta/helpers/is-empty-obj';
import orElse from 'volta/helpers/or-else';
import { CollectionViewResourceName, viewIsLocked } from 'volta/models/collection-view';
import { normalize } from 'volta/utils/api/serialize-and-push';
import { distinct, indexByFn } from 'volta/utils/array-utils';
import { defaultErrorHandler, getChangesetError } from 'volta/utils/error-utils';
import { niFilter } from 'volta/utils/filters-utils';
import { changesetHasChanges } from 'volta/utils/object-utils';
import CollectionViewSettingsSection from './section';
import { get } from '@ember/helper';

import type Owner from '@ember/owner';
import type { BufferedChangeset } from 'ember-changeset/types';
import type { IntlService } from 'ember-intl';
import type CollectionView from 'volta/models/collection-view';
import type {
  ICollectionArgs,
  ICollectionFilter,
  ICollectionGroupOption,
  ICollectionProperty,
  ICollectionSortOption,
  ICollectionViewDefinition,
  ICollectionViewEditCmd,
  TCollectionPropertyForUI,
  TCollectionType,
  TCreationMode
} from 'volta/models/types/collection-view';
import type User from 'volta/models/user';
import type UserProfile from 'volta/models/user-profile';
import type CustomFieldDefinitionsService from 'volta/services/custom-field-definitions-service';
import type SessionUserService from 'volta/services/session-user';
import type StoreService from 'volta/services/store';
import type { IJsonApiQuery } from 'volta/utils/api/jsonapi-types';
import type { TError } from 'volta/utils/error-utils';
import type { SelectedOption } from 'volta/components/bv-select-create';

interface CollectionViewSettingsSignature {
  Args: {
    isCreateMode: boolean;
    view?: CollectionView;
    filterDefinitions: TFilterDefinition[];
    collectionDefinition: ICollectionViewDefinition;
    settingsSection: string;
    metadata?: object;
    groupByOptions: ICollectionGroupOption[];
    enableGrouping?: boolean;
    enableAttributes?: boolean;
    sortOptions: ICollectionSortOption[];
    belowAppHeader?: boolean;
    onSortDirectionChange: (sort: ICollectionSortOption) => void;
    onSortSelectionChange: (option: ICollectionSortOption, checked: boolean) => void;
    onSortOptionsReorder?: (sortBy: ICollectionSortOption[]) => void;
    onFilterChange?: (value: TFilterValue, field: string) => void;
    onPropertyChange?: (props: ICollectionProperty[]) => void;
    onGroupByChange: (group: ICollectionGroupOption, checked: boolean) => void;
    onComponentArgsChange?: (settings: ICollectionArgs) => void;
    onGroupByOptionsReorder?: (groupBy: ICollectionGroupOption[]) => void;
    onCollectionTypeChange?: (type: TCollectionType) => void;
    onCreate?: () => void;
    onEdit?: () => void;
    onClose: () => void;
    onDelete?: () => void;
    changeset: BufferedChangeset & ICollectionViewEditCmd;
  };
  Element: HTMLElement;
}

export default class CollectionViewSettingsComponent extends Component<CollectionViewSettingsSignature> {
  @service intl!: IntlService;
  @service store!: StoreService;
  @service sessionUser!: SessionUserService;
  @service customFieldDefinitionsService!: CustomFieldDefinitionsService;

  @tracked creationMode?: TCreationMode;
  @tracked templates: Partial<CollectionView>[] = [this.args.collectionDefinition.defaultView];
  @tracked selectedTemplateId?: string | 'default';
  // eslint-disable-next-line ember/no-tracked-properties-from-args
  @tracked settingsSection: string | undefined = this.args.settingsSection;
  @tracked selectedUsers: UserProfile[] = [];
  @tracked usersList: (User | UserProfile)[] = this.args.view?.showForUsers?.slice() ?? [];
  @tracked lockedIsChecked = false;
  @tracked lockedIsDirty = false;

  resourceName = CollectionViewResourceName;
  elementId = guidFor(this);

  get lockedError() {
    if (!this.lockedIsChecked || this.args.isCreateMode) {
      return undefined;
    }

    return (this.cs.get('showForRoles') ?? []).length > 0 ||
      (this.cs.get('showForUsers') ?? []).length > 0
      ? undefined
      : this.intl.t('collectionViewsPage.errorDefaultTemplate');
  }

  get cs() {
    return this.args.changeset;
  }

  get selectedTemplate() {
    return this.templates.find(
      (template) => (template.id ?? 'default') === this.selectedTemplateId
    );
  }

  get isLocked() {
    if (this.args.isCreateMode || !this.args.view) {
      return false;
    }
    return viewIsLocked(this.args.view, this.sessionUser.userId);
  }

  get showViewPanel() {
    return Boolean(this.args.collectionDefinition.argsComponent);
  }

  get typeBlocks() {
    type TTypeBlock = { value: TCollectionType; title: string; icon: string };
    return (
      [
        {
          value: 'TABLE',
          title: this.intl.t('collectionViewsPage.types.table'),
          icon: 'table-view'
        },
        {
          value: 'KANBAN',
          title: this.intl.t('collectionViewsPage.types.kanban'),
          icon: 'kanban-view'
        },
        {
          value: 'TIMELINE',
          title: this.intl.t('collectionViewsPage.types.timeline'),
          icon: 'timeline-view'
        },
        { value: 'LIST', title: this.intl.t('collectionViewsPage.types.list'), icon: 'list-view' }
      ] as TTypeBlock[]
    ).filter((type: TTypeBlock) => this.args.collectionDefinition.types.includes(type.value));
  }

  @cached
  get indexedDefinitions() {
    return indexByFn(this.args.collectionDefinition.definitions, (d) => d.property ?? d.valuePath);
  }

  @cached
  get indexedCurrentProperties() {
    return indexByFn(
      this.args.changeset.get('properties') as ICollectionProperty[],
      ({ property }) => property
    );
  }

  @cached
  get properties() {
    const props = this.indexedCurrentProperties;
    const defs = this.indexedDefinitions;
    const properties: TCollectionPropertyForUI[] = [];

    Object.values(props).forEach((p) => {
      const def = defs[p.property];
      if (def) {
        properties.push({ ...p, color: def.color, disabled: false, isStatic: def.isStatic });
      }
    });

    Object.values(defs).forEach((d) => {
      const key = d.property ?? d.valuePath;
      if (key) {
        const notPresent = Boolean(!props[key]);
        if (this.args.isCreateMode || notPresent) {
          properties.push({
            property: key,
            title: d.columnName,
            isCustomField: d.isCustomField ?? false,
            disabled: this.args.isCreateMode ? false : notPresent,
            isFixed: d.isFixed,
            isGroupable: d.isGroupable,
            color: d.color
          });
        }
      }
    });

    return properties;
  }

  constructor(owner: Owner, args: CollectionViewSettingsSignature['Args']) {
    super(owner, args);
    const { view } = this.args;
    this.lockedIsChecked = view
      ? ((view.showForUsers ?? []).length > 0 || (view.showForRoles ?? []).length > 0) &&
        !view.isTemplate
      : false;
  }

  @action
  selectTemplate(selectedTemplate: CollectionView) {
    this.selectedTemplateId = selectedTemplate.id ?? 'default';
    const template: ICollectionViewEditCmd | undefined = {
      ...selectedTemplate.toCreateCmd(),
      ...selectedTemplate.toShareCmd()
    };
    Object.entries(template).forEach(([key, value]) => {
      this.args.changeset.set(key, value);
    });
  }

  @action
  toggleAccordeon(id: string, checked: boolean) {
    this.settingsSection = checked ? id : undefined;
  }

  @action
  changeCreationMode(mode: unknown) {
    this.creationMode = mode as TCreationMode;
    if (mode === 'manual') {
      this.args.changeset.rollback();
    }
  }

  @action
  decorateUserQuery(query: IJsonApiQuery) {
    const { filter = {} } = query;
    filter['userType'] = niFilter(['SUPPLIER', 'API']);
    query.filter = filter;
    return query;
  }

  @action
  setUser(userId: string, [_user]: [User]) {
    this.args.changeset.set(
      'showForUsers',
      distinct([...(this.args.changeset.get('showForUsers') ?? []), userId]).flat()
    );
  }

  @action
  removeUsers() {
    const users = indexByFn(this.selectedUsers, (u) => u.id!);
    this.usersList = this.usersList.filter((u) => u.id && !users[u.id]);
    this.args.changeset.set(
      'showForUsers',
      this.usersList.map((u) => u.id)
    );
    this.selectedUsers = [];
  }

  @action
  changeShowForRoles(ids: string[]) {
    this.args.changeset.set('showForRoles', ids);
    this.args.changeset.set('isTemplate', !this.lockedIsChecked);
  }

  @action
  changeShowForUsers(ids: string[], users: SelectedOption<User>[]) {
    this.usersList = users as User[];
    this.args.changeset.set('showForUsers', ids);
    this.args.changeset.set('isTemplate', !this.lockedIsChecked);
  }

  @action
  changeLocked(checked: boolean) {
    this.lockedIsChecked = checked;
    this.lockedIsDirty = true;
    this.args.changeset.set('isTemplate', !this.lockedIsChecked);
  }

  @action
  onCreate() {
    if (this.creationMode === 'template') {
      this.args.changeset.set(
        'name',
        this.args.changeset.get('name') + ` (${this.intl.t('copySingular')})`
      );
    }

    this.args.onCreate?.();

    if (this.args.changeset.isValid) {
      this.args.onClose();
    }
  }

  @action
  onEdit() {
    this.args.onEdit?.();
    this.args.onClose();
  }

  @action
  scrollToSection(section: string = 'general') {
    const elementToScrollTo = document.getElementById(this.sectionId(section));

    if (elementToScrollTo) {
      elementToScrollTo.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest'
      });
    }
  }

  // Tasks

  fetchTemplates = restartableTask(async () => {
    try {
      const defaultView = this.store.createRecord<CollectionView>('collection-view', {
        ...this.args.collectionDefinition.defaultView
      });
      const data = await this.store.rawQuery(
        'collection-view',
        ['mine', defaultView.entityType as string, 'templates'],
        { include: 'showForUsers,showForRoles' }
      );
      const templates = normalize('collection-view', data) as unknown as CollectionView[];
      this.templates = [defaultView, ...templates];
      this.selectTemplate(defaultView);
      return templates;
    } catch (error) {
      defaultErrorHandler(error as TError);
      return;
    }
  });

  // helpers
  initialValue(filters: ICollectionFilter[] = [], queryParam: string) {
    return filters.find((filter: ICollectionFilter) => filter.property === queryParam);
  }

  sectionId = (id: string) => `${this.elementId}${id}`;

  <template>
    {{#let
      (if
        @isCreateMode
        (cannot 'CreateCollectionView' 'collectionViews')
        (cannot 'UpdateCollectionView' 'collectionViews')
      )
      (component
        CollectionViewSettingsSection
        onToggle=this.toggleAccordeon
        settingsSection=this.settingsSection
      )
      (and (not this.isLocked) @view.id)
      (or @changeset.isPristine @changeset.isInvalid (isPresent this.lockedError))
      as |cannotUpsert Section canBeSaved disabledSaveButton|
    }}
      <BvModal
        @belowAppHeader={{orElse @belowAppHeader true}}
        @translucentBackdrop={{true}}
        @sheet={{true}}
        @size='sm'
        @onClose={{@onClose}}
        @shouldSelfFocus={{false}}
        @onInsert={{fn this.scrollToSection this.settingsSection}}
        ...attributes
        as |modal|
      >
        <modal.Header
          @title={{if
            @isCreateMode
            (t 'collectionViewsPage.create')
            (t 'collectionViewsPage.edit')
          }}
        />
        <modal.Body @sectioned={{false}} @scrollable={{true}} as |mb|>
          {{#if @isCreateMode}}
            <mb.Section @subdued={{true}}>
              <BvChoiceBlocks
                @items={{array
                  (hash
                    value='manual'
                    description=(t 'collectionViewsPage.startNew.title')
                    hoveredDescription=(t 'collectionViewsPage.startNew.description')
                  )
                  (hash
                    value='template'
                    description=(t 'collectionViewsPage.fromTemplate.title')
                    hoveredDescription=(t 'collectionViewsPage.fromTemplate.description')
                  )
                }}
                @onChange={{this.changeCreationMode}}
                @checked={{this.creationMode}}
                @helpText={{t 'collectionViewsPage.templateHelpText'}}
                @auto={{true}}
                @maxColumns='2'
                @size='md'
                class='u-my-3'
              />
            </mb.Section>
          {{/if}}
          {{#if (eq this.creationMode 'template')}}
            {{! template-lint-disable no-at-ember-render-modifiers }}
            <mb.Section @subdued={{true}} {{didInsert (perform this.fetchTemplates)}}>
              <BvSelect
                @options={{this.templates}}
                @selected={{this.selectedTemplate}}
                @onChange={{this.selectTemplate}}
                @fill={{true}}
                @isLoading={{this.fetchTemplates.isRunning}}
                as |template|
              >
                <BvStack>
                  <BvText @variant='heading-sm' @text={{template.name}} class='u-mb-1' />
                  <BvText @text={{template.description}} @variant='body-sm' />
                </BvStack>
              </BvSelect>
            </mb.Section>
          {{else if (or (eq this.creationMode 'manual') (not @isCreateMode))}}
            <mb.Section @subdued={{true}} @flush={{true}} id={{this.sectionId 'general'}}>
              <Section
                @section='general'
                @updated={{changesetHasChanges @changeset 'name' 'description' 'collectionType'}}
              >
                <BvFormLayout as |fl|>
                  <fl.Item>
                    <BvTextField
                      @value={{@changeset.name}}
                      @isEdited={{changesetHasChanges @changeset 'name'}}
                      @label={{t 'name'}}
                      @name='view-name'
                      @onChange={{changesetSet @changeset 'name'}}
                      @error={{getChangesetError @changeset 'name'}}
                      @focused={{true}}
                      @disabled={{or this.isLocked cannotUpsert}}
                    />
                  </fl.Item>
                  <fl.Item>
                    <BvTextField
                      @multiline={{true}}
                      @value={{@changeset.description}}
                      @isEdited={{changesetHasChanges @changeset 'description'}}
                      @error={{getChangesetError @changeset 'description'}}
                      @label={{t 'collectionViewsPage.templateDescription'}}
                      @onChange={{changesetSet @changeset 'description'}}
                      @helpText={{t 'collectionViewsPage.descriptionHelpText'}}
                      @disabled={{or this.isLocked cannotUpsert}}
                      @name='view-description'
                    />
                  </fl.Item>
                  {{#if (gt this.typeBlocks.length 1)}}
                    <fl.Item>
                      <BvChoiceBlocks
                        @items={{this.typeBlocks}}
                        @vertical={{true}}
                        @onChange={{@onCollectionTypeChange}}
                        @checked={{@changeset.collectionType}}
                        @auto={{true}}
                        @maxColumns='{{this.typeBlocks.length}}'
                        @disabled={{cannotUpsert}}
                        @size='md'
                        class='u-mb-4'
                      />
                    </fl.Item>
                  {{/if}}
                </BvFormLayout>
              </Section>
            </mb.Section>
            {{#unless @isCreateMode}}
              {{#if this.showViewPanel}}
                <mb.Section @flush={{true}} @subdued={{true}} id={{this.sectionId 'view'}}>
                  <Section
                    @section='view'
                    @title='settings'
                    @updated={{not (isEmptyObj (get @changeset.change 'args'))}}
                  >
                    {{#let
                      (component
                        (ensureSafeComponent @collectionDefinition.argsComponent this)
                        changeset=@changeset
                        settings=@changeset.args
                        metadata=@metadata
                        onChange=(optional @onComponentArgsChange)
                      )
                      as |ArgsComponent|
                    }}
                      <ArgsComponent />
                    {{/let}}
                  </Section>
                </mb.Section>
              {{/if}}
              {{#if (orElse @enableAttributes true)}}
                <mb.Section @flush={{true}} @subdued={{true}} id={{this.sectionId 'attributes'}}>
                  <Section
                    @section='attributes'
                    @updated={{changesetHasChanges @changeset 'properties'}}
                  >
                    <BvCard>
                      <CollectionViewProperties
                        @properties={{this.properties}}
                        @groupBy={{@changeset.groupBy}}
                        @onChange={{@onPropertyChange}}
                      />
                    </BvCard>
                  </Section>
                </mb.Section>
              {{/if}}
              {{#if @enableGrouping}}
                <mb.Section @flush={{true}} @subdued={{true}} id={{this.sectionId 'groupBy'}}>
                  <Section
                    @section='groupBy'
                    @title='group'
                    @updated={{changesetHasChanges @changeset 'groupBy'}}
                  >
                    <BvCard>
                      <CollectionViewGroups
                        @options={{@groupByOptions}}
                        @groupLimit={{@collectionDefinition.groupLimit}}
                        @groupMin={{@collectionDefinition.groupMin}}
                        @onChange={{@onGroupByChange}}
                        @onReorder={{@onGroupByOptionsReorder}}
                      />
                    </BvCard>
                  </Section>
                </mb.Section>
              {{/if}}
              <mb.Section @flush={{true}} @subdued={{true}} id={{this.sectionId 'sort'}}>
                <Section @section='sort' @updated={{changesetHasChanges @changeset 'sortBy'}}>
                  <BvCard>
                    <CollectionViewSortList
                      @onDirectionChange={{@onSortDirectionChange}}
                      @onSelectionChange={{@onSortSelectionChange}}
                      @onReorderOptions={{@onSortOptionsReorder}}
                      @options={{@sortOptions}}
                      @sortLimit={{@collectionDefinition.sortLimit}}
                    />
                  </BvCard>
                </Section>
              </mb.Section>
              {{#if @collectionDefinition.filters.length}}
                <mb.Section @flush={{true}} @subdued={{true}} id={{this.sectionId 'filter'}}>
                  <Section @section='filter' @updated={{changesetHasChanges @changeset 'filters'}}>
                    <BvCard>
                      <CollectionViewFilters
                        @filters={{@collectionDefinition.filters}}
                        @filterValues={{orElse @changeset.filters (array)}}
                        @resourceName={{this.resourceName}}
                        @onChange={{optional @onFilterChange}}
                      />
                    </BvCard>
                  </Section>
                </mb.Section>
              {{/if}}
              {{#if canBeSaved}}
                <mb.Section @flush={{true}} @subdued={{true}} id={{this.sectionId 'sharing'}}>
                  <Section @section='sharing'>
                    <BvFormLayout as |fl|>
                      <fl.Item>
                        <BvText @color='subdued' @text={{t 'collectionViewsPage.sharingDesc'}} />
                      </fl.Item>
                      <fl.Item>
                        <BvCard as |card|>
                          <card.Section>
                            <UserRoleSelect
                              @label={{t 'collectionViewsPage.showForRoles'}}
                              @multiple={{true}}
                              @fill={{true}}
                              @values={{@changeset.showForRoles}}
                              @onChange={{this.changeShowForRoles}}
                              @disabled={{cannot 'shareCollectionView' 'collectionViews'}}
                              @isEdited={{changesetHasChanges @changeset 'showForRoles'}}
                              @dataName='show-for-roles'
                            />
                          </card.Section>
                          <card.Section>
                            <DomainUserSelect
                              @label={{t 'collectionViewsPage.showForUsers'}}
                              @excludedOptions={{array this.sessionUser.user}}
                              @beforeQuery={{this.decorateUserQuery}}
                              @values={{@changeset.showForUsers}}
                              @multiple={{true}}
                              @fill={{true}}
                              @onChange={{this.changeShowForUsers}}
                              @disabled={{cannot 'shareCollectionView' 'collectionViews'}}
                              @isEdited={{changesetHasChanges @changeset 'showForUsers'}}
                              @dataName='show-for-users'
                              @selectedItemComponent={{component UserListItem isSelected=true}}
                              as |user|
                            >
                              <UserListItem @option={{user}} />
                            </DomainUserSelect>
                          </card.Section>
                        </BvCard>
                      </fl.Item>
                      {{#if
                        (or
                          this.lockedIsChecked
                          this.lockedIsDirty
                          @changeset.showForRoles.length
                          @changeset.showForUsers.length
                        )
                      }}
                        <fl.Item>
                          <BvCheckbox
                            @label={{t 'collectionViewsPage.isTemplate'}}
                            @error={{this.lockedError}}
                            @checked={{this.lockedIsChecked}}
                            @onChange={{this.changeLocked}}
                            @disabled={{cannot 'shareCollectionView' 'collectionViews'}}
                          />
                        </fl.Item>
                        {{#if this.lockedIsChecked}}
                          <fl.Item>
                            <BvBanner
                              @status='warning'
                              @embedded={{true}}
                              @description={{t 'collectionViewsPage.templateWarning'}}
                            />
                          </fl.Item>
                        {{/if}}
                      {{/if}}
                    </BvFormLayout>
                  </Section>
                </mb.Section>
              {{/if}}
            {{/unless}}
          {{/if}}
        </modal.Body>
        <modal.Footer>
          <BvInline @blockAlign='center' @align='space-between' class='u-full-width'>
            {{#if @isCreateMode}}
              <BvBtn @onClick={{@onClose}} @text={{t 'cancel'}} @testName='cancel-edit-view' />
              {{#unless cannotUpsert}}
                <BvBtn
                  @onClick={{this.onCreate}}
                  @color='primary'
                  @iconLeft='create'
                  @tooltip={{t 'create'}}
                  @testName='save-view'
                  @disabled={{disabledSaveButton}}
                />
              {{/unless}}
            {{else}}
              <BvInline @gap='2' @blockAlign='center'>
                {{#unless cannotUpsert}}
                  <BvBtn
                    @onClick={{this.onEdit}}
                    @outline={{true}}
                    @iconLeft={{if canBeSaved 'save-outline' 'copy-outline'}}
                    @tooltip={{t (if canBeSaved 'save' 'duplicate')}}
                    @testName='save-view'
                    @disabled={{disabledSaveButton}}
                  />
                {{/unless}}
                {{#if (and canBeSaved @onDelete (can 'DeleteCollectionView' 'collectionViews'))}}
                  <BvBtn
                    @onClick={{@onDelete}}
                    @color='danger'
                    @outline={{true}}
                    @tooltip={{t 'delete'}}
                    @iconLeft='trash-outline'
                    @testName='delete-view'
                  />
                {{/if}}
              </BvInline>
              <BvBtn
                @color='primary'
                @onClick={{@onClose}}
                @text={{t 'bvFilters.done'}}
                @testName='cancel-edit-view'
              />
            {{/if}}
          </BvInline>
        </modal.Footer>
      </BvModal>
    {{/let}}
  </template>
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'CollectionView::Settings': typeof CollectionViewSettingsComponent;
    'collection-view/settings': typeof CollectionViewSettingsComponent;
  }
}
